import React, { useState, useEffect } from "react";
import styled from "styled-components";
import DOMPurify from "dompurify";
import ENV_CONFIG from "../config/environment-unified";
import useTranslation from "../hooks/useTranslation";

const LegalContentDisplay = ({ type }) => {
  const { t } = useTranslation();
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchLegalContent = async () => {
      try {
        setLoading(true);
        setError(null);

        let endpoint;
        switch (type) {
          case "rgpd":
            endpoint = ENV_CONFIG.LEGAL_RGPD_ENDPOINT;
            break;
          case "cgu":
            endpoint = ENV_CONFIG.LEGAL_CGU_ENDPOINT;
            break;
          case "faq":
            endpoint = ENV_CONFIG.LEGAL_FAQ_ENDPOINT;
            break;
          case "mentions-legales":
            endpoint = ENV_CONFIG.LEGAL_MENTIONS_ENDPOINT;
            break;
          default:
            throw new Error(`Unknown legal content type: ${type}`);
        }

        console.log(`Fetching legal content from: ${endpoint}`);

        const response = await fetch(endpoint, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
        });

        if (!response.ok) {
          throw new Error(`Failed to fetch legal content: ${response.status}`);
        }

        const data = await response.json();

        if (!data.success || !data.data) {
          throw new Error("Invalid response format");
        }

        setContent(data.data);
      } catch (err) {
        console.error("Error fetching legal content:", err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchLegalContent();
  }, [type]);

  if (loading) {
    return (
      <LoadingContainer>
        <LoadingText>{t('legal.loadingContent')}</LoadingText>
      </LoadingContainer>
    );
  }

  if (error) {
    return (
      <ErrorContainer>
        <ErrorTitle>{t('legal.errorTitle')}</ErrorTitle>
        <ErrorText>
          {t('legal.errorMessage')}
        </ErrorText>
        <ErrorDetails>{error}</ErrorDetails>
      </ErrorContainer>
    );
  }

  if (!content) {
    return (
      <ErrorContainer>
        <ErrorTitle>{t('legal.contentUnavailable')}</ErrorTitle>
        <ErrorText>{t('legal.contentUnavailableMessage')}</ErrorText>
      </ErrorContainer>
    );
  }

  // Sanitize HTML content
  const sanitizedContent = DOMPurify.sanitize(content.content, {
    ALLOWED_TAGS: [
      "h1",
      "h2",
      "h3",
      "h4",
      "h5",
      "h6",
      "p",
      "br",
      "ul",
      "ol",
      "li",
      "strong",
      "em",
      "u",
      "a",
      "span",
      "div",
    ],
    ALLOWED_ATTR: ["href", "target", "rel", "class"],
  });

  return (
    <ContentWrapper>
      <ContentTitle>{content.title}</ContentTitle>

      {content.updated_at && (
        <LastUpdated>
          {t('legal.lastUpdated')}{" "}
          {new Date(content.updated_at).toLocaleDateString("fr-FR", {
            year: "numeric",
            month: "long",
            day: "numeric",
          })}
        </LastUpdated>
      )}

      <ContentBody dangerouslySetInnerHTML={{ __html: sanitizedContent }} />

      {content.contact && (
        <ContactSection>
          <ContactTitle>{t('legal.contact')}</ContactTitle>
          <ContactInfo>
            {content.contact.email && (
              <ContactItem>
                <ContactLabel>{t('legal.email')}</ContactLabel>
                <ContactLink href={`mailto:${content.contact.email}`}>
                  {content.contact.email}
                </ContactLink>
              </ContactItem>
            )}
            {content.contact.phone && (
              <ContactItem>
                <ContactLabel>{t('legal.phone')}</ContactLabel>
                <ContactValue>{content.contact.phone}</ContactValue>
              </ContactItem>
            )}
            {content.contact.address && (
              <ContactItem>
                <ContactLabel>{t('legal.address')}</ContactLabel>
                <ContactValue>{content.contact.address}</ContactValue>
              </ContactItem>
            )}
          </ContactInfo>
        </ContactSection>
      )}

      {content.version && (
        <VersionInfo>{t('legal.version')} {content.version}</VersionInfo>
      )}
    </ContentWrapper>
  );
};

export default LegalContentDisplay;

// Styled Components
const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 300px;
  padding: 1.5rem;
`;

const LoadingText = styled.p`
  font-size: 18px;
  color: ${({ theme }) => theme.color.darkerText};
`;

const ErrorContainer = styled.div`
  padding: 2rem;
  margin: 2rem 0;
  background-color: ${({ theme }) => theme.color.secondaryBg};
  border-radius: 12px;
  border: 1px solid ${({ theme }) => theme.color.border};
`;

const ErrorTitle = styled.h2`
  color: ${({ theme }) => theme.color.error || "#d32f2f"};
  margin-bottom: 1rem;
  font-size: 24px;
`;

const ErrorText = styled.p`
  color: ${({ theme }) => theme.color.darkerText};
  margin-bottom: 0.5rem;
  font-size: 16px;
  line-height: 1.6;
`;

const ErrorDetails = styled.p`
  color: ${({ theme }) => theme.color.text};
  font-size: 14px;
  font-style: italic;
  margin-top: 1rem;
  opacity: 0.7;
`;

const ContentWrapper = styled.div`
  width: 100%;
`;

const ContentTitle = styled.h1`
  margin-left: 2rem;
  padding-top: 1.5rem;
  padding-bottom: 1.5rem;
  font-size: 28px;
  font-weight: 700;
  color: ${({ theme }) => theme.color.primary};
`;

const LastUpdated = styled.p`
  margin-left: 2rem;
  margin-bottom: 1.5rem;
  font-size: 13px;
  color: ${({ theme }) => theme.color.text};
  opacity: 0.7;
  font-style: italic;
`;

const ContentBody = styled.div`
  margin: auto;
  max-width: 90%;
  color: ${({ theme }) => theme.color.darkerText};

  h2 {
    margin-left: 1rem;
    margin-top: 2rem;
    margin-bottom: 1rem;
    font-size: 24px;
    font-weight: 600;
    color: ${({ theme }) => theme.color.darkerText};
  }

  h3 {
    margin-left: 1rem;
    margin-top: 1.5rem;
    margin-bottom: 0.75rem;
    font-size: 20px;
    font-weight: 600;
    color: ${({ theme }) => theme.color.darkerText};
  }

  h4,
  h5,
  h6 {
    margin-left: 1rem;
    margin-top: 1rem;
    margin-bottom: 0.5rem;
    font-weight: 600;
    color: ${({ theme }) => theme.color.darkerText};
  }

  p {
    font-size: 14px;
    line-height: 28px;
    margin-top: 1rem;
    margin-bottom: 1rem;
  }

  ul,
  ol {
    margin-left: 2rem;
    margin-top: 1rem;
    margin-bottom: 1rem;
    padding-left: 1rem;
  }

  li {
    font-size: 14px;
    line-height: 24px;
    margin-bottom: 0.5rem;
  }

  a {
    color: ${({ theme }) => theme.color.primary};
    text-decoration: underline;

    &:hover {
      color: ${({ theme }) => theme.color.primaryHover || theme.color.primary};
      opacity: 0.8;
    }
  }

  strong {
    font-weight: 600;
  }

  em {
    font-style: italic;
  }
`;

const ContactSection = styled.div`
  margin: 2.5rem auto;
  max-width: 90%;
  padding: 2rem;
  background-color: ${({ theme }) => theme.color.secondaryBg};
  border-radius: 12px;
  border: 1px solid ${({ theme }) => theme.color.border};
`;

const ContactTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  color: ${({ theme }) => theme.color.primary};
  margin-bottom: 1.5rem;
`;

const ContactInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const ContactItem = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
`;

const ContactLabel = styled.span`
  font-weight: 600;
  color: ${({ theme }) => theme.color.darkerText};
  min-width: 100px;
`;

const ContactValue = styled.span`
  color: ${({ theme }) => theme.color.text};
`;

const ContactLink = styled.a`
  color: ${({ theme }) => theme.color.primary};
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`;

const VersionInfo = styled.p`
  margin: 1.5rem auto;
  max-width: 90%;
  font-size: 11px;
  color: ${({ theme }) => theme.color.text};
  opacity: 0.5;
  text-align: right;
`;

